import React, { createContext, useContext, useReducer, useCallback } from 'react';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';

/**
 * AdminContextValue interface definition
 * @typedef {Object} AdminContextValue
 * @property {boolean} isAdminView - Whether currently in admin view mode
 * @property {StudentInfo|null} selectedStudent - Currently selected student information
 * @property {UserInfo} adminUser - Admin user information
 * @property {boolean} canPerformActions - Whether admin can perform actions (future extensibility)
 * @property {Function} selectStudent - Function to select a student
 * @property {Function} clearSelectedStudent - Function to clear selected student
 * @property {Function} exitAdminView - Function to exit admin view mode
 * @property {Function} enterAdminView - Function to enter admin view mode
 */

/**
 * StudentInfo interface definition
 * @typedef {Object} StudentInfo
 * @property {string} id - Student ID
 * @property {string} name - Student full name
 * @property {string} email - Student email address
 * @property {string[]} programs - Array of program names student is enrolled in
 * @property {Date} [lastActivity] - Last activity timestamp
 */

/**
 * UserInfo interface definition
 * @typedef {Object} UserInfo
 * @property {string} id - User ID
 * @property {string} name - User full name
 * @property {string} email - User email address
 * @property {string[]} roles - Array of user roles
 */

// Admin state action types
const ADMIN_ACTIONS = {
  ENTER_ADMIN_VIEW: 'ENTER_ADMIN_VIEW',
  EXIT_ADMIN_VIEW: 'EXIT_ADMIN_VIEW',
  SELECT_STUDENT: 'SELECT_STUDENT',
  CLEAR_SELECTED_STUDENT: 'CLEAR_SELECTED_STUDENT',
  SET_CAN_PERFORM_ACTIONS: 'SET_CAN_PERFORM_ACTIONS'
};

// Initial admin state
const initialAdminState = {
  isAdminView: false,
  selectedStudent: null,
  canPerformActions: false // Future extensibility for admin actions
};

/**
 * Admin state reducer
 * @param {Object} state - Current admin state
 * @param {Object} action - Action to perform
 * @returns {Object} New admin state
 */
function adminReducer(state, action) {
  switch (action.type) {
    case ADMIN_ACTIONS.ENTER_ADMIN_VIEW:
      return {
        ...state,
        isAdminView: true
      };
    
    case ADMIN_ACTIONS.EXIT_ADMIN_VIEW:
      return {
        ...initialAdminState,
        isAdminView: false
      };
    
    case ADMIN_ACTIONS.SELECT_STUDENT:
      return {
        ...state,
        selectedStudent: action.payload
      };
    
    case ADMIN_ACTIONS.CLEAR_SELECTED_STUDENT:
      return {
        ...state,
        selectedStudent: null
      };
    
    case ADMIN_ACTIONS.SET_CAN_PERFORM_ACTIONS:
      return {
        ...state,
        canPerformActions: action.payload
      };
    
    default:
      return state;
  }
}

// Create the admin context
const AdminContext = createContext(null);

/**
 * AdminContextProvider component
 * Provides admin view state management throughout the application
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AdminContextProvider component
 */
export function AdminContextProvider({ children }) {
  const [adminState, dispatch] = useReducer(adminReducer, initialAdminState);
  
  // Get admin user information from authentication
  const { 
    user, 
    roles, 
    isAnyAdmin,
    isLoggedIn 
  } = useRoleAuthentication();

  // Create admin user object
  const adminUser = {
    id: user?.id || user?.sub || '',
    name: user?.name || '',
    email: user?.email || '',
    roles: roles || []
  };

  // Action creators
  const enterAdminView = useCallback(() => {
    dispatch({ type: ADMIN_ACTIONS.ENTER_ADMIN_VIEW });
  }, []);

  const exitAdminView = useCallback(() => {
    dispatch({ type: ADMIN_ACTIONS.EXIT_ADMIN_VIEW });
  }, []);

  const selectStudent = useCallback((studentInfo) => {
    dispatch({ 
      type: ADMIN_ACTIONS.SELECT_STUDENT, 
      payload: studentInfo 
    });
  }, []);

  const clearSelectedStudent = useCallback(() => {
    dispatch({ type: ADMIN_ACTIONS.CLEAR_SELECTED_STUDENT });
  }, []);

  const setCanPerformActions = useCallback((canPerform) => {
    dispatch({ 
      type: ADMIN_ACTIONS.SET_CAN_PERFORM_ACTIONS, 
      payload: canPerform 
    });
  }, []);

  // Context value
  const contextValue = {
    // State
    isAdminView: adminState.isAdminView,
    selectedStudent: adminState.selectedStudent,
    adminUser,
    canPerformActions: adminState.canPerformActions,
    
    // Actions
    selectStudent,
    clearSelectedStudent,
    exitAdminView,
    enterAdminView,
    setCanPerformActions,
    
    // Authentication info
    isLoggedIn,
    isAnyAdmin
  };

  return (
    <AdminContext.Provider value={contextValue}>
      {children}
    </AdminContext.Provider>
  );
}

/**
 * Hook to access admin context
 * @returns {AdminContextValue} Admin context value
 * @throws {Error} If used outside AdminContextProvider
 */
export function useAdminContext() {
  const context = useContext(AdminContext);
  
  if (context === null) {
    throw new Error('useAdminContext must be used within an AdminContextProvider');
  }
  
  return context;
}

export default AdminContext;